import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiX, FiCheck, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import SearchableDropdown from '../../../components/ui/SearchableDropdown';
import TagInput from '../../../components/ui/TagInput';
import './GLChartOfAccountsTab.css';

// =============================================
// Setup GL Accounts — "Edit account" popup for ONE account of the staged list (nothing
// is in the Chart of Accounts yet; that happens on Create). Opens by clicking the
// account. Collects what the Chart of Accounts tab asks for, so an account is not
// created as a bare number + name: account type (section), detail type (the
// financial-statement category it reports under), sub-account of, description.
// A preview at the bottom shows where the account will sit.
// =============================================
export type AccountKind = 'asset' | 'liability' | 'equity' | 'income' | 'other_income' | 'cogs' | 'expense';

export interface StagedAccount {
  key: string;
  checked: boolean;
  account_kind: AccountKind;
  account_name: string;
  suggested_number: string;
  account_description: string;
  /** other names the account is known by (comma list) */
  alias_names: string;
  /** free labels to group / find accounts by (comma list) */
  tags: string;
  finsta_cate_id: number;
  normal_balance: 'Debit' | 'Credit';
  /** already present in the owner's Chart of Accounts */
  exists: boolean;
  /** key of the staged account this one is a sub-account of (same section), or null */
  parent_key: string | null;
}

export interface StagedAccountSection {
  key: string;
  name: string;
  normal_balance: 'Debit' | 'Credit';
  rows: StagedAccount[];
}

export interface StatementCategory {
  cate_id: number;
  header_id: number;
  cate_name: string;
}

// Statement header (acctg_finsta_headers) each section reports under; Cost of Goods Sold
// is one category of the Expense header and has a section of its own.
const SECTION_HEADER: Record<string, number> = { asset: 1, liability: 2, equity: 3, income: 4, cogs: 5, expense: 5 };
const COGS_CATE_ID = 13;
const OTHER_INCOME_CATE_ID = 12;

/** The statement categories an account of this section can report under. */
export function categoriesForSection(sectionKey: string, categories: StatementCategory[]): StatementCategory[] {
  const header = SECTION_HEADER[sectionKey];
  return categories.filter(c => Number(c.header_id) === header
    && (sectionKey === 'cogs' ? Number(c.cate_id) === COGS_CATE_ID
      : sectionKey === 'expense' ? Number(c.cate_id) !== COGS_CATE_ID : true));
}

/** account_kind follows the section, except "Other Income" inside the Income section. */
export function kindFor(sectionKey: string, cateId: number): AccountKind {
  if (sectionKey === 'income') return Number(cateId) === OTHER_INCOME_CATE_ID ? 'other_income' : 'income';
  return (sectionKey as AccountKind);
}

/** Rows in display order: each account followed by its sub-accounts, with its depth. */
export function orderWithDepth(rows: StagedAccount[]): { row: StagedAccount; depth: number }[] {
  const keys = new Set(rows.map(r => r.key));
  const out: { row: StagedAccount; depth: number }[] = [];
  const seen = new Set<string>();
  const walk = (parentKey: string | null, depth: number) => {
    rows.forEach(r => {
      const p = r.parent_key && keys.has(r.parent_key) ? r.parent_key : null;
      if (p !== parentKey || seen.has(r.key)) return;
      seen.add(r.key);
      out.push({ row: r, depth });
      walk(r.key, depth + 1);
    });
  };
  walk(null, 0);
  rows.forEach(r => { if (!seen.has(r.key)) out.push({ row: r, depth: 0 }); }); // a loop, never expected
  return out;
}

interface Props {
  /** The account being edited (a blank row = adding a new one). */
  account: StagedAccount;
  sectionKey: string;
  sections: StagedAccountSection[];
  categories: StatementCategory[];
  isNew: boolean;
  /** Position among all the accounts, for the ‹ › arrows ("3 of 41"); omitted when adding. */
  position?: { index: number; total: number };
  /** `step` -1 / +1 = keep the changes and open the previous / next account. */
  onSave: (next: StagedAccount, sectionKey: string, step?: -1 | 1) => void;
  onClose: () => void;
}

const cleanList = (raw: string) => Array.from(new Set(raw.split(',').map(x => x.trim()).filter(Boolean))).join(', ');

const StagedAccountModal: React.FC<Props> = ({ account, sectionKey, sections, categories, isNew, position, onSave, onClose }) => {
  const [name, setName] = useState(account.account_name);
  const [number, setNumber] = useState(account.suggested_number);
  const [secKey, setSecKey] = useState(sectionKey);
  const [cateId, setCateId] = useState<number>(account.finsta_cate_id);
  const [isSub, setIsSub] = useState(!!account.parent_key);
  const [parentKey, setParentKey] = useState<string>(account.parent_key || '');
  const [description, setDescription] = useState(account.account_description || '');
  const [aliasNames, setAliasNames] = useState(account.alias_names || '');
  const [tags, setTags] = useState(account.tags || '');
  const [include, setInclude] = useState(account.checked);
  const [errors, setErrors] = useState<{ name?: string; parent?: string; cate?: string }>({});
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => { nameRef.current?.focus(); }, []);
  // Esc closes without saving.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const section = sections.find(s => s.key === secKey) || sections[0];
  const cateOptions = useMemo(() => categoriesForSection(secKey, categories), [secKey, categories]);

  // Moving to another account type: the detail type and the parent belong to the old one.
  const changeSection = (next: string) => {
    setSecKey(next);
    const allowed = categoriesForSection(next, categories);
    if (!allowed.some(c => Number(c.cate_id) === Number(cateId))) setCateId(allowed[0] ? Number(allowed[0].cate_id) : cateId);
    setParentKey(''); setIsSub(false);
  };

  // Possible parents: named accounts of the same section, except this one and anything
  // already below it (an account cannot sit under its own sub-account).
  const parentOptions = useMemo(() => {
    const below = new Set<string>([account.key]);
    for (let size = 0; size !== below.size;) {
      size = below.size;
      section.rows.forEach(r => { if (r.parent_key && below.has(r.parent_key)) below.add(r.key); });
    }
    return orderWithDepth(section.rows.filter(r => r.account_name.trim() && !below.has(r.key)))
      .map(({ row, depth }) => ({
        value: row.key,
        label: `${row.suggested_number ? row.suggested_number + ' ' : ''}${row.account_name}`,
        depth,
      }));
  }, [section, account.key]);

  const draft: StagedAccount = {
    ...account,
    account_name: name.trim(),
    suggested_number: number.trim(),
    account_description: description.trim(),
    alias_names: cleanList(aliasNames),
    tags: cleanList(tags),
    finsta_cate_id: Number(cateId),
    account_kind: kindFor(secKey, Number(cateId)),
    normal_balance: section.normal_balance,
    parent_key: isSub && parentKey ? parentKey : null,
    checked: include,
  };

  // Where it will sit: its section with this account in place (as typed so far).
  const preview = useMemo(() => {
    const rows = section.rows.filter(r => r.key !== account.key && r.account_name.trim());
    const shown = { ...draft, account_name: draft.account_name || '(new account)' };
    return orderWithDepth([...rows, shown]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, account.key, name, number, isSub, parentKey]);

  const save = (step?: -1 | 1) => {
    const errs: typeof errors = {};
    if (!name.trim()) errs.name = 'Enter the account name.';
    if (!cateOptions.some(c => Number(c.cate_id) === Number(cateId))) errs.cate = 'Choose the detail type.';
    if (isSub && !parentKey) errs.parent = 'Choose the parent account, or untick "Make this a sub-account".';
    const dup = sections.some(s => s.rows.some(r => r.key !== account.key
      && r.account_name.trim().toLowerCase() === name.trim().toLowerCase()
      && s.key === secKey && (r.parent_key || null) === (isSub && parentKey ? parentKey : null)));
    if (!errs.name && dup) errs.name = 'There is already an account with this name here.';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    onSave(draft, secKey, step);
  };

  return (
    <div className="coa-acct-overlay" onMouseDown={onClose}>
      <div className="coa-acct-modal" role="dialog" aria-modal="true" aria-labelledby="coa-acct-title"
        onMouseDown={e => e.stopPropagation()}>
        <div className="coa-acct-head">
          <h3 id="coa-acct-title">{isNew ? 'Add account' : 'Edit account'}</h3>
          {position && (
            <div className="coa-acct-nav">
              <button type="button" className="coa-acct-close" disabled={position.index <= 0}
                title="Keep changes and open the previous account" aria-label="Previous account"
                onClick={() => save(-1)}><FiChevronLeft size={18} /></button>
              <span>{position.index + 1} of {position.total}</span>
              <button type="button" className="coa-acct-close" disabled={position.index >= position.total - 1}
                title="Keep changes and open the next account" aria-label="Next account"
                onClick={() => save(1)}><FiChevronRight size={18} /></button>
            </div>
          )}
          <button type="button" className="coa-acct-close" onClick={onClose} title="Close" aria-label="Close"><FiX size={18} /></button>
        </div>

        <form className="coa-acct-body" onSubmit={e => { e.preventDefault(); save(); }}>
          <div className="coa-acct-row">
            <div className="coa-field-group" style={{ flex: '0 0 120px' }}>
              <label className="coa-field-label" htmlFor="coa-acct-number">Number</label>
              <input id="coa-acct-number" className="coa-acct-input" type="text" value={number} maxLength={20}
                onChange={e => setNumber(e.target.value)} />
            </div>
            <div className="coa-field-group" style={{ flex: 1 }}>
              <label className="coa-field-label" htmlFor="coa-acct-name">Account name <span className="coa-required">*</span></label>
              <input id="coa-acct-name" ref={nameRef} className={`coa-acct-input ${errors.name ? 'coa-input-error' : ''}`}
                type="text" value={name} maxLength={150} onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: undefined })); }} />
              {errors.name && <span className="coa-field-error">{errors.name}</span>}
            </div>
          </div>

          <div className="coa-acct-row">
            <div className="coa-field-group" style={{ flex: 1 }}>
              <label className="coa-field-label">Account type <span className="coa-required">*</span></label>
              <SearchableDropdown
                options={sections.map(s => ({ value: s.key, label: s.name }))}
                value={secKey}
                onChange={v => { if (v) changeSection(v); }}
              />
            </div>
            <div className="coa-field-group" style={{ flex: 1 }}>
              <label className="coa-field-label">Detail type <span className="coa-required">*</span></label>
              <SearchableDropdown
                options={cateOptions.map(c => ({ value: String(c.cate_id), label: c.cate_name }))}
                value={String(cateId)}
                onChange={v => { if (v) { setCateId(Number(v)); setErrors(p => ({ ...p, cate: undefined })); } }}
                hasError={!!errors.cate}
              />
              {errors.cate && <span className="coa-field-error">{errors.cate}</span>}
            </div>
          </div>
          <span className="coa-field-hint">
            Normal balance: {section.normal_balance}. The detail type is the line of the financial statements this account reports under.
          </span>

          <label className="coa-acct-check">
            <input type="checkbox" checked={isSub}
              onChange={e => { setIsSub(e.target.checked); if (!e.target.checked) { setParentKey(''); setErrors(p => ({ ...p, parent: undefined })); } }} />
            Make this a sub-account
          </label>
          {isSub && (
            <div className="coa-field-group">
              <label className="coa-field-label">Parent account <span className="coa-required">*</span></label>
              <SearchableDropdown
                options={parentOptions}
                value={parentKey}
                onChange={v => { setParentKey(v); setErrors(p => ({ ...p, parent: undefined })); }}
                placeholder="-- Choose the parent account --"
                hasError={!!errors.parent}
              />
              {errors.parent && <span className="coa-field-error">{errors.parent}</span>}
              {parentOptions.length === 0 && <span className="coa-field-hint">No other account in {section.name} yet.</span>}
            </div>
          )}

          <div className="coa-field-group">
            <label className="coa-field-label" htmlFor="coa-acct-desc">Description</label>
            <textarea id="coa-acct-desc" className="coa-acct-input" rows={2} value={description} maxLength={500}
              onChange={e => setDescription(e.target.value)} />
          </div>

          <div className="coa-acct-row">
            <div className="coa-field-group" style={{ flex: 1 }}>
              <label className="coa-field-label" htmlFor="coa-acct-alias">Alias names</label>
              <input id="coa-acct-alias" className="coa-acct-input" type="text" value={aliasNames} maxLength={300}
                placeholder="hydro, power bill" title="Other names for this account, separated by commas"
                onChange={e => setAliasNames(e.target.value)} />
            </div>
            <div className="coa-field-group" style={{ flex: 1 }}>
              <label className="coa-field-label" htmlFor="coa-acct-tags">Tags</label>
              <TagInput id="coa-acct-tags" value={tags} onChange={setTags} placeholder="rental, 118 1SNW" ariaLabel="Tags" />
            </div>
          </div>

          <label className="coa-acct-check">
            <input type="checkbox" checked={include} onChange={e => setInclude(e.target.checked)} />
            Create this account
            {account.exists && <span className="coa-field-hint" style={{ marginLeft: 6 }}>(already in your Chart of Accounts)</span>}
          </label>

          <div className="coa-acct-preview">
            <div className="coa-acct-preview-head">
              <strong>{section.name}</strong>
              <span className="coa-dropdown-badge">Preview</span>
            </div>
            <div className="coa-acct-preview-list">
              {preview.map(({ row, depth }) => (
                <div key={row.key} className={`coa-acct-preview-item ${row.key === account.key ? 'current' : ''}`}
                  style={{ paddingLeft: 10 + depth * 18 }}
                  ref={el => { if (el && row.key === account.key) el.scrollIntoView({ block: 'nearest' }); }}>
                  {row.suggested_number && <span className="coa-acct-preview-num">{row.suggested_number}</span>}
                  {row.account_name}
                </div>
              ))}
            </div>
          </div>

          <div className="coa-acct-foot">
            <button type="button" className="coa-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="coa-btn-primary"><FiCheck size={15} /> {isNew ? 'Add account' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StagedAccountModal;
